import { api } from "../api";
import { ProjectEntity, ProjectInput } from "./projects.types";
import { ProjectMinimalResponse } from "../../types/api/getProject";

/**
 * Obtener todos los proyectos sin paginación (solo id y título)
 */
export const getAllProjectsMinimal = async (): Promise<
  ProjectMinimalResponse[]
> => {
  try {
    const response = await api.get("/projects/all");
    return response.status === 200 ? response.data : [];
  } catch (error) {
    console.error("Error al obtener proyectos:", error);
    return [];
  }
};

/**
 * Obtener un proyecto por ID
 *
 * Propaga el error a propósito. Devolver `null` ante cualquier fallo hacía que
 * quien llamaba lo leyera como "el proyecto no existe": el guardado terminaba
 * avisando éxito y descartando en silencio las lecciones del proyecto.
 */
export const getProjectById = async (id: number): Promise<ProjectEntity> => {
  try {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error al obtener proyecto con ID ${id}:`, error);
    throw error;
  }
};

/**
 * Crear un nuevo proyecto
 */
export const createProject = async (
  project: ProjectInput,
): Promise<ProjectEntity | null> => {
  try {
    const response = await api.post("/projects", project);
    return response.data;
  } catch (error) {
    console.error("Error al crear proyecto:", error);
    throw error;
  }
};

/**
 * Actualizar un proyecto existente
 */
export const updateProject = async (
  id: number,
  project: Partial<ProjectInput>,
): Promise<ProjectEntity | null> => {
  try {
    const response = await api.patch(`/projects/${id}`, project);
    return response.data;
  } catch (error) {
    console.error(`Error al actualizar proyecto con ID ${id}:`, error);
    throw error;
  }
};

/**
 * Agregar programas a un proyecto
 */
export const addProgramsToProject = async (
  projectId: number,
  programIds: number[],
): Promise<void> => {
  try {
    await api.post(`/projects/${projectId}/programs`, {
      program_ids: programIds,
    });
  } catch (error) {
    console.error(
      `Error al agregar programas al proyecto ${projectId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Eliminar un programa de un proyecto
 */
export const removeProgramFromProject = async (
  projectId: number,
  programId: number,
): Promise<void> => {
  try {
    await api.delete(`/projects/${projectId}/programs/${programId}`);
  } catch (error) {
    console.error(
      `Error al eliminar programa del proyecto ${projectId}:`,
      error,
    );
    throw error;
  }
};

export const reorderProjectCourses = async (
  projectId: number,
  courses: Array<{ course_id: number; order_by: number }>,
): Promise<void> => {
  try {
    await api.patch(`/projects/${projectId}/courses/reorder`, { courses });
  } catch (error) {
    console.error(`Error al reordenar cursos del proyecto ${projectId}:`, error);
    throw error;
  }
};

export const updateProjectCourseProgram = async (
  projectId: number,
  courseId: number,
  programId: number | null,
): Promise<void> => {
  try {
    await api.patch(`/projects/${projectId}/courses/${courseId}`, {
      program_id: programId,
    });
  } catch (error) {
    console.error(
      `Error al actualizar programa del curso ${courseId} en proyecto ${projectId}:`,
      error,
    );
    throw error;
  }
};

export const addCoursesToProject = async (
  projectId: number,
  coursesInput:
    | number[]
    | Array<{ course_id: number; program_id?: number | null }>,
): Promise<void> => {
  try {
    // Convertir a formato esperado por el backend
    const courses = Array.isArray(coursesInput)
      ? coursesInput[0] && typeof coursesInput[0] === "object"
        ? coursesInput
        : coursesInput.map((id) => ({ course_id: id }))
      : [];

    await api.post(`/projects/${projectId}/courses`, { courses });
  } catch (error) {
    console.error(`Error al agregar cursos al proyecto ${projectId}:`, error);
    throw error;
  }
};

/**
 * Sincronizar cursos de un proyecto
 * Compara los cursos actuales con los nuevos y agrega/elimina según corresponda
 */
export const syncProjectCourses = async (
  projectId: number,
  input:
    | number[]
    | {
        courses?: Array<{ course_id: number; program_id?: number | null }>;
        course_ids?: number[];
      },
): Promise<void> => {
  try {
    // Obtener el proyecto actual con sus cursos
    const project = await getProjectById(projectId);

    const currentCourseIds = project.courses?.map((c) => c.id) || [];

    // Extraer los course IDs del input
    let newCourses: Array<{ course_id: number; program_id?: number | null }> =
      [];
    if (Array.isArray(input)) {
      newCourses = input.map((id) => ({ course_id: id }));
    } else if (input.courses) {
      newCourses = input.courses;
    } else if (input.course_ids) {
      newCourses = input.course_ids.map((id) => ({ course_id: id }));
    }

    const newCourseIds = newCourses.map((c) => c.course_id);

    // Identificar cursos a agregar (están en newCourseIds pero no en currentCourseIds)
    const coursesToAdd = newCourses.filter(
      (c) => !currentCourseIds.includes(c.course_id),
    );

    // Identificar cursos existentes cuyo program_id cambió
    const coursesToUpdate = newCourses.filter((c) => {
      if (!currentCourseIds.includes(c.course_id)) return false;
      const current = project.courses?.find(
        (cur) => cur.id === c.course_id,
      );
      return current !== undefined && current.program_id !== (c.program_id ?? null);
    });

    // Identificar cursos a eliminar (están en currentCourseIds pero no en newCourseIds)
    const coursesToRemove = currentCourseIds.filter(
      (id) => !newCourseIds.includes(id),
    );

    // Eliminar cursos que ya no están
    for (const courseId of coursesToRemove) {
      await removeCourseFromProject(projectId, courseId);
    }

    // Agregar nuevos cursos
    if (coursesToAdd.length > 0) {
      await addCoursesToProject(projectId, coursesToAdd);
    }

    // Actualizar program_id de cursos existentes
    for (const c of coursesToUpdate) {
      await updateProjectCourseProgram(
        projectId,
        c.course_id,
        c.program_id ?? null,
      );
    }

    // Persistir el orden actual de todos los cursos
    const reorderPayload = newCourses.map((c, index) => ({
      course_id: c.course_id,
      order_by: index + 1,
    }));
    await reorderProjectCourses(projectId, reorderPayload);
  } catch (error) {
    console.error(
      `Error al sincronizar cursos del proyecto ${projectId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Eliminar un curso de un proyecto
 */
export const removeCourseFromProject = async (
  projectId: number,
  courseId: number,
): Promise<void> => {
  try {
    await api.delete(`/projects/${projectId}/courses/${courseId}`);
  } catch (error) {
    console.error(`Error al eliminar curso del proyecto ${projectId}:`, error);
    throw error;
  }
};

/**
 * Agregar lecciones a un proyecto
 */
export const addLessonsToProject = async (
  projectId: number,
  lessonIds: number[],
): Promise<void> => {
  try {
    await api.post(`/projects/${projectId}/lessons`, { lesson_ids: lessonIds });
  } catch (error) {
    console.error(
      `Error al agregar lecciones al proyecto ${projectId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Sincronizar lecciones de un proyecto
 * Compara las lecciones actuales con las nuevas y agrega/elimina según corresponda
 */
export const syncProjectLessons = async (
  projectId: number,
  newLessonIds: number[],
): Promise<void> => {
  try {
    // Obtener el proyecto actual con sus lecciones
    const project = await getProjectById(projectId);

    const currentLessonIds = project.lessons?.map((l) => l.id) || [];

    // Identificar lecciones a agregar (están en newLessonIds pero no en currentLessonIds)
    const lessonsToAdd = newLessonIds.filter(
      (id) => !currentLessonIds.includes(id),
    );

    // Identificar lecciones a eliminar (están en currentLessonIds pero no en newLessonIds)
    const lessonsToRemove = currentLessonIds.filter(
      (id) => !newLessonIds.includes(id),
    );

    // Eliminar lecciones que ya no están
    for (const lessonId of lessonsToRemove) {
      await removeLessonFromProject(projectId, lessonId);
    }

    // Agregar nuevas lecciones
    if (lessonsToAdd.length > 0) {
      await addLessonsToProject(projectId, lessonsToAdd);
    }
  } catch (error) {
    console.error(
      `Error al sincronizar lecciones del proyecto ${projectId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Eliminar una lección de un proyecto
 */
export const removeLessonFromProject = async (
  projectId: number,
  lessonId: number,
): Promise<void> => {
  try {
    await api.delete(`/projects/${projectId}/lessons/${lessonId}`);
  } catch (error) {
    console.error(
      `Error al eliminar lección del proyecto ${projectId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Reemplazar de una sola vez el conjunto de lecciones de un proyecto.
 *
 * Una única llamada, transaccional en el backend. Reemplaza al viejo
 * "borrar todas y volver a agregarlas de a una", que dejaba el proyecto sin
 * lecciones si fallaba alguna de las llamadas intermedias. Las lecciones que
 * salen se desvinculan; ninguna fila se borra.
 */
export const replaceProjectLessons = async (
  projectId: number,
  lessons: Array<{ lesson_id: number; phase_id: number }>,
): Promise<void> => {
  try {
    await api.put(`/projects/${projectId}/lessons`, { lessons });
  } catch (error) {
    console.error(
      `Error al reemplazar las lecciones del proyecto ${projectId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Remover TODAS las lecciones de un proyecto (hard delete para evitar conflictos)
 */
export const removeAllLessonsFromProject = async (
  projectId: number,
): Promise<void> => {
  try {
    await api.delete(`/projects/${projectId}/lessons`);
  } catch (error) {
    console.error(
      `Error al eliminar todas las lecciones del proyecto ${projectId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Agregar una evaluación a un proyecto
 */
export const addEvaluationToProject = async (
  projectId: number,
  evaluation: {
    key_learnings?: string;
    rubric_achieved?: string;
    rubric_in_process?: string;
    rubric_needs_support?: string;
    axis?: string;
  },
): Promise<void> => {
  try {
    await api.post(`/projects/${projectId}/evaluations`, evaluation);
  } catch (error) {
    console.error(
      `Error al agregar evaluación al proyecto ${projectId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Actualizar una evaluación de un proyecto
 */
export const updateProjectEvaluation = async (
  projectId: number,
  evaluationId: number,
  evaluation: {
    key_learnings?: string;
    rubric_achieved?: string;
    rubric_in_process?: string;
    rubric_needs_support?: string;
    axis?: string;
  },
): Promise<void> => {
  try {
    await api.patch(
      `/projects/${projectId}/evaluations/${evaluationId}`,
      evaluation,
    );
  } catch (error) {
    console.error(
      `Error al actualizar evaluación del proyecto ${projectId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Eliminar una evaluación de un proyecto
 */
export const deleteProjectEvaluation = async (
  projectId: number,
  evaluationId: number,
): Promise<void> => {
  try {
    await api.delete(`/projects/${projectId}/evaluations/${evaluationId}`);
  } catch (error) {
    console.error(
      `Error al eliminar evaluación del proyecto ${projectId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Actualizar el título de una fase del proyecto
 */
export const updatePhaseTitle = async (
  projectId: number,
  phaseId: number,
  title: string,
): Promise<void> => {
  try {
    await api.patch(`/projects/${projectId}/phases/${phaseId}`, { title });
  } catch (error) {
    console.error(`Error al actualizar título de fase ${phaseId}:`, error);
    throw error;
  }
};

/**
 * Agregar lecciones a una fase específica del proyecto
 */
export const addLessonsToPhase = async (
  projectId: number,
  phaseId: number,
  lessonIds: number[],
): Promise<void> => {
  try {
    await api.post(`/projects/${projectId}/lessons`, {
      lesson_ids: lessonIds,
      phase_id: phaseId,
    });
  } catch (error) {
    console.error(`Error al agregar lecciones a la fase ${phaseId}:`, error);
    throw error;
  }
};

/**
 * Mover una lección a otra fase
 */
export const moveLessonToPhase = async (
  projectId: number,
  lessonId: number,
  phaseId: number,
  orderBy: number,
): Promise<void> => {
  try {
    await api.patch(`/projects/${projectId}/lessons/${lessonId}/phase`, {
      phase_id: phaseId,
      order_by: orderBy,
    });
  } catch (error) {
    console.error(
      `Error al mover lección ${lessonId} a fase ${phaseId}:`,
      error,
    );
    throw error;
  }
};
